/**
 * Sf1034SignatureDialog — Digital signature capture for the CORE Buy Program
 * SF1034 form. (#510)
 *
 * Supports two modes:
 *  - Pre-generation (onCapture): the signature is returned to the caller to embed
 *    in the document payload so it is applied during SF 1034 generation.
 *  - Post-generation (transactionId + onSigned): the signature is recorded on an
 *    existing transaction via POST /api/rest/data/v1/sf1034/approve.
 *
 * The underlying SignatureCaptureDialog supports both on-screen (canvas) and
 * ePad device signature capture.
 */
import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import { SignatureCaptureDialog } from '@/components/shared/SignatureCaptureDialog';
import { useNotifications } from '@/stores/useNotificationStore';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';

export interface Sf1034SignatureDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The transaction entity ID to attach the signature to (post-generation mode). */
  transactionId?: string | null;
  /**
   * Pre-generation capture mode. When provided, the captured signature is returned
   * to the caller (to embed in the document payload before generation) instead of
   * being recorded against an existing transaction.
   */
  onCapture?: (signatureBase64Png: string) => void;
  /** Called when the user successfully signs and the signature is recorded. */
  onSigned?: () => void;
  /** Called when the user skips or dismisses without signing. */
  onSkip: () => void;
}

export const Sf1034SignatureDialog: React.FC<Sf1034SignatureDialogProps> = ({
  open,
  transactionId,
  onCapture,
  onSigned,
  onSkip,
}) => {
  const [capturePadOpen, setCapturePadOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notifications = useNotifications();
  const isPreGeneration = typeof onCapture === 'function';

  const handleOpenCapture = useCallback(() => {
    setCapturePadOpen(true);
  }, []);

  const handleSignatureSaved = useCallback(
    async (signatureBase64Png: string) => {
      // Pre-generation mode: hand the signature back to embed in the payload.
      if (onCapture) {
        setCapturePadOpen(false);
        onCapture(signatureBase64Png);
        return;
      }

      if (!transactionId) return;

      setCapturePadOpen(false);
      setIsSaving(true);
      setError(null);

      try {
        await httpClient.post(API_CONFIG.ENDPOINTS.SF1034_APPROVE, {
          body: JSON.stringify({
            transactionId,
            signatureValue: signatureBase64Png,
          }),
        });
        notifications.success(
          'SF 1034 Signed',
          'Your digital signature has been recorded on the SF 1034.',
        );
        onSigned?.();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to record signature';
        setError(msg);
        notifications.error('Signature failed', msg);
      } finally {
        setIsSaving(false);
      }
    },
    [transactionId, onCapture, notifications, onSigned],
  );

  return (
    <>
      <Dialog open={open && !capturePadOpen} onClose={onSkip} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DrawIcon color="primary" />
            <Typography variant="h6">Digitally Sign SF 1034</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isPreGeneration
              ? 'An SF 1034 will be generated for this Buy Program issue. Add your digital signature now so it is applied to the document during generation.'
              : 'Your SF 1034 has been generated for this Buy Program issue. Please review the attached document and add your digital signature to complete the approval.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click <strong>Sign Document</strong> below to capture your signature. The form will be
            considered signed immediately after you complete the signature action.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onSkip} color="inherit" disabled={isSaving}>
            Skip Signature
          </Button>
          <Button
            onClick={handleOpenCapture}
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <DrawIcon />}
            disabled={isSaving || (!isPreGeneration && !transactionId)}
          >
            {isSaving ? 'Recording…' : 'Sign Document'}
          </Button>
        </DialogActions>
      </Dialog>

      <SignatureCaptureDialog
        open={capturePadOpen}
        title="Capture SF 1034 Digital Signature"
        onCancel={() => setCapturePadOpen(false)}
        onSave={(sig) => void handleSignatureSaved(sig)}
      />
    </>
  );
};
